/* SAM-1237 — the lists the location form needs, read through crud (tenant-scoped on the server). */
import { useQuery } from '@tanstack/react-query';
import { defaultInstance } from '../../../api/httpClient';
import { useEstateGroups } from '../../../hooks/useEstates';

export interface Option { id: number; name: string }

const list = (body: unknown): Record<string, unknown>[] => {
  const data = (body as { data?: unknown } | null)?.data ?? body;
  return Array.isArray(data) ? (data as Record<string, unknown>[]) : [];
};
const toOption = (r: Record<string, unknown>): Option => ({ id: Number(r.id), name: String(r.name ?? r.id) });

/** The estate groups of the tenant, by name. */
export const useLocationGroups = () => {
  const { allEstateGroups, isLoading } = useEstateGroups({ limit: 500 });
  const groups: Option[] = (allEstateGroups as Array<{ id?: number; name?: string }>)
    .filter((g) => g.id != null)
    .map((g) => ({ id: Number(g.id), name: g.name ?? String(g.id) }));
  return { groups, isLoading };
};

/** Buildings of a group; empty while none is picked. */
export const useGroupBuildings = (groupId: number | null) =>
  useQuery<Option[]>({
    queryKey: ['location-buildings', groupId],
    enabled: groupId != null,
    staleTime: 60_000,
    queryFn: async () => {
      const res = await defaultInstance({ url: `/building?estate_group_id=${groupId}&limit=500&order_by=name&order_direction=ASC`, method: 'GET' });
      return list(res).map(toOption);
    },
  });

/** Units or shops of a group (of a building when the group has them), matching what was typed. */
export const useGroupUnits = (groupId: number | null, buildingId: number | null, search: string, enabled: boolean) =>
  useQuery<Option[]>({
    queryKey: ['location-units', groupId, buildingId, search],
    enabled: enabled && groupId != null,
    staleTime: 30_000,
    queryFn: async () => {
      const q = new URLSearchParams({ estate_group_id: String(groupId), limit: '20', order_by: 'name', order_direction: 'ASC' });
      if (buildingId != null) q.set('building_id', String(buildingId));
      if (search.trim()) q.set('name_ilike', search.trim());
      const res = await defaultInstance({ url: `/estate?${q.toString()}`, method: 'GET' });
      return list(res).map(toOption);
    },
  });

/** One unit by id, for the read view and the selected value. */
export const useUnitName = (unitId: number | null) =>
  useQuery<string>({
    queryKey: ['location-unit', unitId],
    enabled: unitId != null,
    staleTime: 60_000,
    queryFn: async () => {
      const res = await defaultInstance({ url: `/estate/${unitId}`, method: 'GET' });
      const row = list(res)[0] ?? ((res as { data?: Record<string, unknown> } | null)?.data ?? {});
      return String((row as Record<string, unknown>).name ?? unitId);
    },
  });

/** The primary estate group of a business (null when none): the group of its location. */
export const usePrimaryGroupId = (businessId: string | null | undefined) =>
  useQuery<number | null>({
    queryKey: ['business-primary-group', businessId],
    enabled: !!businessId,
    staleTime: 10_000,
    queryFn: async () => {
      const res = await defaultInstance({ url: `/business_estate_group?business_id=${encodeURIComponent(String(businessId))}&is_primary=true&limit=5`, method: 'GET' });
      const row = list(res)[0];
      return row?.estate_group_id != null ? Number(row.estate_group_id) : null;
    },
  });
